import axios from 'axios';
import { useEffect, useState } from 'react';
import API_URL from '../config/api';
import './Dashboard.css';

const MyRewards = ({ user }) => {
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  // Calculate cash value from points (same as Header/Dashboard)
  const calculateRewardValue = (points) => {
    const amount = points / 10000; // 10,000 points = $1 USD
    return {
      amount: amount.toFixed(2),
      formattedAmount: `$${amount.toFixed(2)}`
    };
  };

  useEffect(() => {
    fetchTransactions();
  }, []);

  const fetchTransactions = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_URL}/users/transactions`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      // Backend returns { transactions: [...], pagination: {...}, summary: {...} }
      setTransactions(response.data.transactions || []);
      setLoading(false);
    } catch (err) {
      console.error('Fetch transactions error:', err);
      setError(err.response?.data?.message || 'Failed to load transactions');
      setLoading(false);
    }
  };

  const getTransactionIcon = (transaction) => {
    // Use source-specific icons if available in details
    if (transaction.details?.source) {
      switch (transaction.details.source) {
        case 'profile_question':
          return '📝';
        case 'survey_completion':
          return '🎯';
        case 'reward_redemption':
          return '🎁';
        case 'bonus':
          return '🎉';
        case 'manual_adjustment':
          return '⚡';
        default:
          break;
      }
    }

    // Fallback to type-based icons
    switch (transaction.type) {
      case 'earned':
        return '✅';
      case 'spent':
        return '🎁';
      case 'bonus':
        return '🎉';
      case 'refund':
        return '↩️';
      default:
        return '💰';
    }
  };

  const getTransactionTitle = (transaction) => {
    if (transaction.details?.source) {
      switch (transaction.details.source) {
        case 'profile_question':
          return 'Profile Question Answered';
        case 'survey_completion':
          return 'Survey Completed';
        case 'reward_redemption':
          return 'Reward Redeemed';
        case 'bonus':
          return 'Bonus Awarded';
        case 'manual_adjustment':
          return 'Manual Adjustment';
        default:
          break;
      }
    }

    // Fallback to description or type
    return transaction.description.split(' ')[0] + ' ' + transaction.description.split(' ')[1] ||
      transaction.type.charAt(0).toUpperCase() + transaction.type.slice(1);
  };

  const formatTransactionDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getTransactionColor = (type) => {
    switch (type) {
      case 'earned':
      case 'bonus':
      case 'refund':
        return 'positive';
      case 'spent':
        return 'negative';
      default:
        return '';
    }
  };

  if (loading) {
    return (
      <div className="dashboard-container">
        <div className="loading">
          <div className="spinner"></div>
          <p>Loading your rewards...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="dashboard-container">
      <div className="dashboard-header">
        <h1>My Rewards</h1>
        <p>View your transaction history and reward redemptions</p>
      </div>

      {error && <div className="alert alert-danger">{error}</div>}

      <div className="transactions-section">
        <div className="transactions-header">
          <div className="transactions-title">
            <h2>Transaction History</h2>
          </div>
          <div className="transactions-balance">
            <div className="balance-label">Current Balance</div>
            <div className="balance-value">
              {(user?.pointsBalance || 0).toLocaleString('en-US')} pts
              {calculateRewardValue(user?.pointsBalance || 0).amount >= 0.01 && (
                <span style={{ fontSize: '0.85em', color: '#888' }}> ({calculateRewardValue(user?.pointsBalance || 0).formattedAmount})</span>
              )}
            </div>
          </div>
        </div>
        {transactions.length === 0 ? (
          <div className="no-data">
            <p>No transactions yet. Complete surveys to earn rewards!</p>
          </div>
        ) : (
          <div className="transactions-list">
            {transactions.map((transaction) => (
              <div key={transaction._id} className={`transaction-card enhanced ${getTransactionColor(transaction.type)}`}>
                <div className="transaction-icon">
                  {getTransactionIcon(transaction)}
                </div>
                <div className="transaction-details">
                  <h4 className="transaction-title">{getTransactionTitle(transaction)}</h4>

                  {/* Enhanced details based on transaction source */}
                  {transaction.details?.source === 'profile_question' && (
                    <div className="transaction-metadata">
                      <p className="transaction-category">
                        {transaction.details.qualificationCategory || 'Profile Question'}
                      </p>
                      <p className="transaction-question">
                        Q: {transaction.details.questionText}
                      </p>
                      {transaction.details.userAnswer?.answerText && (
                        <p className="transaction-answer">
                          Your Answer: {Array.isArray(transaction.details.userAnswer.answerText)
                            ? transaction.details.userAnswer.answerText.join(', ')
                            : transaction.details.userAnswer.answerText}
                        </p>
                      )}
                    </div>
                  )}

                  {transaction.details?.source === 'survey_completion' && (
                    <div className="transaction-metadata">
                      <p className="transaction-category">
                        {transaction.details.surveyTitle}
                      </p>
                      {transaction.details.completionPercentage && (
                        <p className="transaction-completion">
                          Completion: {transaction.details.completionPercentage}%
                        </p>
                      )}
                    </div>
                  )}

                  {transaction.details?.source === 'reward_redemption' && (
                    <div className="transaction-metadata">
                      <p className="transaction-category">
                        {transaction.details.rewardName}
                      </p>
                      <p className="transaction-reward-type">
                        Type: {transaction.details.rewardType}
                      </p>
                    </div>
                  )}

                  {/* Fallback to regular description if no enhanced details */}
                  {!transaction.details?.source && (
                    <p className="transaction-description">{transaction.description}</p>
                  )}

                  <div className="transaction-footer">
                    <p className="transaction-date">
                      {formatTransactionDate(transaction.createdAt)}
                    </p>
                    {transaction.details?.reasonCode && (
                      <span className="transaction-reason">
                        {transaction.details.reasonCode}
                      </span>
                    )}
                  </div>
                </div>
                <div className="transaction-amount">
                  <span className={getTransactionColor(transaction.type)}>
                    {transaction.type === 'earned' || transaction.type === 'bonus' || transaction.type === 'refund' ? '+' : '-'}{transaction.amount.toLocaleString('en-US')} pts
                  </span>
                  {calculateRewardValue(transaction.amount).amount >= 0.01 && (
                    <small style={{ display: 'block', fontSize: '0.8em', color: '#666' }}>
                      ({calculateRewardValue(transaction.amount).formattedAmount})
                    </small>
                  )}
                  <div className="transaction-balance">
                    Balance: {transaction.balanceAfter.toLocaleString('en-US')} pts
                    {calculateRewardValue(transaction.balanceAfter).amount >= 0.01 && (
                      <span style={{ fontSize: '0.85em', color: '#888' }}> ({calculateRewardValue(transaction.balanceAfter).formattedAmount})</span>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default MyRewards;

